/**
 * @file AttentionList.tsx
 * @description Список «что нужно сделать» для сводок кабинета (phase19.31,
 * шаг О4; uiux-design.md §5 «Сводка»).
 *
 * Строка — ссылка 48–56px (высота кнопки темы, у кладовщика 56): значок
 * тона, текст, подпись под ним, число справа и стрелка. Одна строка — один
 * ВИД задачи, а не одно событие: страница сама сводит события в группы.
 * Пустой список — строка «Всё в порядке» с зелёной отметкой, а не пустой блок.
 *
 * ```tsx
 * <AttentionList
 *   aria-label="Что нужно сделать"
 *   items={[{ key: "receipts", tone: "warning", title: "Приёмки не проведены", caption: "самая старая — 16 дней назад", count: 9, to: "…/receipts?status=DRAFT" }]}
 * />
 * ```
 */

import { ChevronRight, CircleCheck, Info, OctagonAlert, TriangleAlert, type LucideIcon } from "lucide-react";
import { Link } from "react-router";

import { Skeleton } from "./skeleton";
import { cn } from "@/lib/utils";

/** Тон строки: «успеть до» (янтарный), «работа стоит» (красный), справка (синий). */
export type AttentionTone = "warning" | "danger" | "info";

export interface AttentionItem {
  key: string;
  tone: AttentionTone;
  title: string;
  /** Подпись под текстом: «самая старая — 16 дней назад». */
  caption?: string;
  /** Число справа; нет — числа нет. */
  count?: number;
  to: string;
}

/** Реестр тонов: значок и его цвет. */
const TONE_META: Record<AttentionTone, { icon: LucideIcon; className: string }> = {
  warning: { icon: TriangleAlert, className: "text-[var(--status-attention-dot)]" },
  danger: { icon: OctagonAlert, className: "text-[var(--status-error-fg)]" },
  info: { icon: Info, className: "text-[var(--status-progress-fg)]" },
};

interface Props {
  items: AttentionItem[];
  loading?: boolean;
  /** Текст пустого списка. */
  emptyText?: string;
  "aria-label"?: string;
  className?: string;
}

export function AttentionList({ items, loading = false, emptyText = "Всё в порядке", className, "aria-label": ariaLabel }: Props) {
  if (loading && items.length === 0) {
    return (
      <div aria-busy="true" className={cn("flex flex-col gap-2", className)}>
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} className="h-12 rounded-lg" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className={cn("type-body flex items-center gap-2 text-on-surface-variant", className)}>
        <CircleCheck aria-hidden className="size-5 shrink-0 text-[var(--success)]" />
        {emptyText}
      </p>
    );
  }

  return (
    <ul aria-label={ariaLabel} className={cn("-mx-2 flex flex-col", className)}>
      {items.map((item) => {
        const meta = TONE_META[item.tone];
        const Icon = meta.icon;
        return (
          <li key={item.key}>
            <Link
              to={item.to}
              data-tone={item.tone}
              className={cn(
                "flex min-h-[var(--control-h,3rem)] items-center gap-3 rounded-lg px-2 py-2 outline-none transition-colors",
                "hover:bg-[var(--row-hover)] active:bg-surface-low focus-visible:ring-2 focus-visible:ring-ring",
              )}
            >
              <Icon aria-hidden className={cn("size-5 shrink-0", meta.className)} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="type-body font-medium text-on-surface">{item.title}</span>
                {item.caption ? <span className="type-caption text-on-surface-variant">{item.caption}</span> : null}
              </span>
              {item.count !== undefined ? (
                <span className="type-body shrink-0 font-semibold tabular-nums text-on-surface">{item.count}</span>
              ) : null}
              <ChevronRight aria-hidden className="size-4 shrink-0 text-on-surface-variant" />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
